import { axiosInstance } from "../../../api/axiosInstance.tsx";

// Helper function to safely parse potentially double-encoded JSON
const safeJSONParse = (value: string | any, defaultValue: any = []) => {
  if (!value) return defaultValue;
  if (typeof value !== 'string') return value;
  
  try {
    let parsed = JSON.parse(value);
    // Check if it's still a string (double-encoded)
    while (typeof parsed === 'string') {
      parsed = JSON.parse(parsed);
    }
    return parsed;
  } catch (error) {
    console.error("Failed to parse JSON:", error, "Value:", value);
    return defaultValue;
  }
};

export interface ahsSearchParams {
  namaAHS?: string;
  ahsSubCode?: string;
  category?: string;
  sumber?: string;
  totalAHS?: string;
  limit?: number;
  offset?: number;
  keyword?: string;
  orderBy?: string;
  order?: string;
}

export interface ahsData {
  namaAHS: string;
  kategoriAHS: string;
  satuanAHS: string;
  kodeAHS: string;
  sumber: string;
  jumlahHargaTenagaKerja: string;
  jumlahHargaBahan: string;
  jumlahHargaPeralatan: string;
  overheadProfitKoefisien: string;
  jumlahOverheadProfitKoefisien: string;
}

export interface ahsItem {
  tenagaKerja?: string;
  descriptionTenagaKerja?: string;
  koefisienTenagaKerja?: string;
  hargaTenagaKerja?: string;
  purchaseUnitTenagaKerja?: string;
  unitPriceIDRTenagaKerja?: string;
  inputUnitTenagaKerja?: string;
  ukuranTenagaKerja?: string;

  bahan?: string;
  descriptionBahan?: string;
  koefisienBahan?: string;
  hargaBahan?: string;
  purchaseUnitBahan?: string;
  unitPriceIDRBahan?: string;
  inputUnitBahan?: string;
  ukuranBahan?: string;

  peralatan?: string;
  descriptionPeralatan?: string;
  koefisienPeralatan?: string;
  hargaPeralatan?: string;
  purchaseUnitPeralatan?: string;
  unitPriceIDRPeralatan?: string;
  inputUnitPeralatan?: string;
  ukuranPeralatan?: string;
}

export const fetchAhs = async (): Promise<any[]> => {
  try {
    const response = await axiosInstance.get("/sales/ahs/view_ahs");
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "Gagal mengambil data ahs. Silakan coba lagi."
    );
  }
};

export const fetchCategoryItem = async (category: string) => {
  const response = await axiosInstance.get("/items/search_items", {
    params: {
      category: category,
      limit: 1000000,
    },
  });
  return response.data;
};

export const fetchItemDetails = async (itemId: string) => {
  const response = await axiosInstance.get("/itemDetails/search_item_details", {
    params: {
      itemId: itemId,
      limit: 1000000,
    },
  });
  return response.data;
};

export const getAllItemDetails = async () => {
  const response = await axiosInstance.get("/itemDetails/view_item_details");
  return response.data;
};

export const getAhs = async (params: ahsSearchParams = {}) => {
  const response = await axiosInstance.get("sales/ahs/search_ahs", {
    params,
  });
  return response.data;
};

// services/ahsService.tsx

import axios from "axios";

export const createAhs = async (data: {
  namaAHS: string;
  kategoriAHS: string;
  satuanAHS: string;
  kodeAHS: string;
  sumber: string;
  hargaSatuan: string;
  jumlahHargaTenagaKerja: string;
  jumlahHargaBahan: string;
  jumlahHargaPeralatan: string;
  overheadProfitKoefisien: string;
  jumlahOverheadProfitKoefisien: string;
  totalJumlahHargaABC: string;
  totalJumlahHargaDE: string;
  totalAHS: string;
  items: Array<{
    idTk?: string;
    tenagaKerja?: string;
    descriptionTenagaKerja?: string;
    koefisienTenagaKerja?: string;
    hargaTenagaKerja?: string;
    purchaseUnitTenagaKerja?: string;
    unitPriceIDRTenagaKerja?: string;
    unitMeasurementTenagaKerja?: any; // Mengubah tipe ke any untuk fleksibilitas
    inputUnitTenagaKerja?: string;
    ukuranTenagaKerja?: string;
    itemId?: string;

    idBahan?: string;
    bahan?: string;
    descriptionBahan?: string;
    koefisienBahan?: string;
    hargaBahan?: string;
    purchaseUnitBahan?: string;
    unitPriceIDRBahan?: string;
    unitMeasurementBahan?: any;
    inputUnitBahan?: string;
    ukuranBahan?: string;

    idPeralatan?: string;
    peralatan?: string;
    descriptionPeralatan?: string;
    koefisienPeralatan?: string;
    hargaPeralatan?: string;
    purchaseUnitPeralatan?: string;
    unitPriceIDRPeralatan?: string;
    unitMeasurementPeralatan?: any;
    inputUnitPeralatan?: string;
    ukuranPeralatan?: string;
  }>;
}) => {
  // Format payload sesuai dengan kebutuhan API
  const payload = {
    namaAHS: data.namaAHS,
    category: data.kategoriAHS,
    satuanAHS: data.satuanAHS,
    ahsSubCode: data.kodeAHS,
    sumber: data.sumber,
    hargaSatuan: data.hargaSatuan,
    jumlahHargaTenagaKerja: data.jumlahHargaTenagaKerja,
    jumlahHargaBahan: data.jumlahHargaBahan,
    jumlahHargaPeralatan: data.jumlahHargaPeralatan,
    overheadKoefisien: data.overheadProfitKoefisien,
    jumlahOverheadProfitKoefisien: data.jumlahOverheadProfitKoefisien,
    totalJumlahHargaABC: data.totalJumlahHargaABC,
    totalJumlahHargaDE: data.totalJumlahHargaDE,
    totalAHS: data.totalAHS,
    items: (Array.isArray(data.items) ? data.items : [data.items]).map(
      (item) => ({
        ...item,
        unitMeasurementTenagaKerja: Array.isArray(item.unitMeasurementTenagaKerja)
          ? JSON.stringify(item.unitMeasurementTenagaKerja)
          : typeof item.unitMeasurementTenagaKerja === "string"
          ? item.unitMeasurementTenagaKerja
          : "[]",
        unitMeasurementBahan: Array.isArray(item.unitMeasurementBahan)
          ? JSON.stringify(item.unitMeasurementBahan)
          : typeof item.unitMeasurementBahan === "string"
          ? item.unitMeasurementBahan
          : "[]",
        unitMeasurementPeralatan: Array.isArray(item.unitMeasurementPeralatan)
          ? JSON.stringify(item.unitMeasurementPeralatan)
          : typeof item.unitMeasurementPeralatan === "string"
          ? item.unitMeasurementPeralatan
          : "[]",
      })
    ),
  };

  const response = await axiosInstance.post("sales/ahs/create_ahs", payload);
  return response.data;
};

// Delete ahs
export const deleteAhs = async (id: string) => {
  try {
    await axiosInstance.delete(`sales/ahs/delete_ahs/${id}`);
  } catch (error) {
    throw (
      error.response?.data?.message || "Terjadi kesalahan saat menghapus AHS"
    );
  }
};

// update ahs
export const updateAhs = async (data: {
  id: string;
  namaAHS: string;
  kategoriAHS: string;
  satuanAHS: string;
  kodeAHS: string;
  sumber: string;
  hargaSatuan: string;
  jumlahHargaTenagaKerja: string;
  jumlahHargaBahan: string;
  jumlahHargaPeralatan: string;
  overheadProfitKoefisien: string;
  jumlahOverheadProfitKoefisien: string;
  totalJumlahHargaABC: string;
  totalJumlahHargaDE: string;
  totalAHS: string;
  items: Array<{
    tenagaKerja?: string;
    descriptionTenagaKerja?: string;
    koefisienTenagaKerja?: string;
    hargaTenagaKerja?: string;
    purchaseUnitTenagaKerja?: string;
    unitPriceIDRTenagaKerja?: string;
    unitMeasurementTenagaKerja?: any; // Mengubah tipe ke any untuk fleksibilitas
    inputUnitTenagaKerja?: string;
    ukuranTenagaKerja?: string;
    itemId?: string;

    bahan?: string;
    descriptionBahan?: string;
    koefisienBahan?: string;
    hargaBahan?: string;
    purchaseUnitBahan?: string;
    unitPriceIDRBahan?: string;
    unitMeasurementBahan?: any;
    inputUnitBahan?: string;
    ukuranBahan?: string;

    peralatan?: string;
    descriptionPeralatan?: string;
    koefisienPeralatan?: string;
    hargaPeralatan?: string;
    purchaseUnitPeralatan?: string;
    unitPriceIDRPeralatan?: string;
    unitMeasurementPeralatan?: any;
    inputUnitPeralatan?: string;
    ukuranPeralatan?: string;
  }>;
}) => {
  // Format payload sesuai dengan kebutuhan API
  const payload = {
    id: data.id,
    namaAHS: data.namaAHS,
    category: data.kategoriAHS,
    satuanAHS: data.satuanAHS,
    ahsSubCode: data.kodeAHS,
    sumber: data.sumber,
    hargaSatuan: data.hargaSatuan,
    jumlahHargaTenagaKerja: data.jumlahHargaTenagaKerja,
    jumlahHargaBahan: data.jumlahHargaBahan,
    jumlahHargaPeralatan: data.jumlahHargaPeralatan,
    overheadKoefisien: data.overheadProfitKoefisien,
    jumlahOverheadProfitKoefisien: data.jumlahOverheadProfitKoefisien,
    totalJumlahHargaABC: data.totalJumlahHargaABC,
    totalJumlahHargaDE: data.totalJumlahHargaDE,
    totalAHS: data.totalAHS,
    items: (Array.isArray(data.items) ? data.items : [data.items]).map(
      (item) => ({
        ...item,
        unitMeasurementTenagaKerja: Array.isArray(item.unitMeasurementTenagaKerja)
          ? JSON.stringify(item.unitMeasurementTenagaKerja)
          : typeof item.unitMeasurementTenagaKerja === "string"
          ? item.unitMeasurementTenagaKerja
          : "[]",
        unitMeasurementBahan: Array.isArray(item.unitMeasurementBahan)
          ? JSON.stringify(item.unitMeasurementBahan)
          : typeof item.unitMeasurementBahan === "string"
          ? item.unitMeasurementBahan
          : "[]",
        unitMeasurementPeralatan: Array.isArray(item.unitMeasurementPeralatan)
          ? JSON.stringify(item.unitMeasurementPeralatan)
          : typeof item.unitMeasurementPeralatan === "string"
          ? item.unitMeasurementPeralatan
          : "[]",
      })
    ),
  };

  const response = await axiosInstance.put(`sales/ahs/update_ahs/${data.id}`, payload);
  return response.data;
};

// Fetch Vendor Details by ID
export const getAhsById = async (id) => {
  try {
    const response = await axiosInstance.get(`sales/ahs/view_ahs/${id}`);
    return response.data;
  } catch (error) {
    throw (
      error.response?.data?.message ||
      "Terjadi kesalahan saat mengambil data ahs"
    );
  }
};

// Fungsi untuk mencari vendor berdasarkan keyword
export const sesarchAhs = async (searchQuery: string) => {
  try {
    const response = await axiosInstance.get(
      `sales/ahs/search_ahs?keyword=${searchQuery}`
    );
    return response.data; // Pastikan response sesuai format yang diterima tabel
  } catch (error) {
    console.error("Error fetching ahs:", error);
    throw (
      error.response?.data?.message ||
      "Terjadi kesalahan saat mengambil data ahs"
    );
  }
};

export const fetchTenagaKerjaItems = async () => {
  try {
    const response = await fetchCategoryItem("Tenaga Kerja");

    if (!response?.data) {
      throw new Error("Data tidak valid");
    }

    // Hanya ambil data dasar untuk dropdown (tidak perlu details di sini)
    return {
      ...response,
      data: response.data.map((item) => ({
        id: item.id,
        name: item.name,
        description: item.description,
        category: item.category,
        code: item.code,
      })),
    };
  } catch (error) {
    console.error("Error fetching Tenaga Kerja:", error);
    throw error;
  }
};

export const fetchTenagaKerjaDetails = async (itemId: string) => {
  try {
    const response = await fetchItemDetails(itemId);

    if (!response?.data?.length) {
      throw new Error("Detail item tidak ditemukan");
    }

    const details = response.data.map((detail) => ({
      id: detail.id,
      itemDetailName: detail.itemDetailName,
      unitPrice: detail.unitPrice,
      purchaseUnitMeasurement: detail.purchaseUnitMeasurement,
      unitMeasurement: safeJSONParse(detail.unitMeasurement, []),
      supplierVendor: detail.supplierVendor,
      itemId: detail.itemId,
    }));
    return details;
  } catch (error) {
    console.error("Error fetching detail:", error);
    throw error;
  }
};

export const fetchBahanItems = async () => {
  try {
    const items = await fetchCategoryItem("Material");
    console.log("API Response:", items); // Inspect the actual structure
    return items;
  } catch (error) {
    console.error("Gagal mengambil data Material:", error);
    throw error;
  }
};

export const fetchBahanDetails = async (itemId: string) => {
  try {
    const response = await fetchItemDetails(itemId);

    if (!response?.data?.length) {
      throw new Error("Detail item tidak ditemukan");
    }

    const details = response.data.map((detail) => ({
      id: detail.id,
      itemDetailName: detail.itemDetailName,
      unitPrice: detail.unitPrice,
      purchaseUnitMeasurement: detail.purchaseUnitMeasurement,
      unitMeasurement: safeJSONParse(detail.unitMeasurement, []),
      supplierVendor: detail.supplierVendor,
      itemId: detail.itemId,
    }));
    return details;
  } catch (error) {
    console.error("Error fetching detail:", error);
    throw error;
  }
};

export const fetchPeralatanItems = async () => {
  try {
    const items = await fetchCategoryItem("Peralatan");
    console.log("API Response:", items); // Inspect the actual structure
    return items;
  } catch (error) {
    console.error("Gagal mengambil data Peralatan:", error);
    throw error;
  }
};

export const fetchPeralatanDetails = async (itemId: string) => {
  try {
    const response = await fetchItemDetails(itemId);

    if (!response?.data?.length) {
      throw new Error("Detail item tidak ditemukan");
    }

    const details = response.data.map((detail) => ({
      id: detail.id,
      itemDetailName: detail.itemDetailName,
      unitPrice: detail.unitPrice,
      purchaseUnitMeasurement: detail.purchaseUnitMeasurement,
      unitMeasurement: safeJSONParse(detail.unitMeasurement, []),
      supplierVendor: detail.supplierVendor,
      itemId: detail.itemId,
    }));
    return details;
  } catch (error) {
    console.error("Error fetching detail:", error);
    throw error;
  }
};

export const duplicateAhs = async (id: string, newName?: string) => {
  try {
    const response = await axiosInstance.post(`sales/ahs/duplicate_ahs/${id}`, {
      newName: newName ?? "",
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message || "Terjadi kesalahan saat menduplikasi AHS"
    );
  }
};